import {Navigate, Route, Routes, useLocation} from "react-router-dom";
import {useEffect, useState} from "react";
import {getFooter, getNavbar} from "./services/HomepageService";
import ScrollToTop from "./hooks/ScrollToTop";
import {Colors} from "./constants/Colors";
import SplashScreen from "./components/ui/splash-screen/SplashScreen";
import LoadingSpinner from "./components/ui/loading-spinner/LoadingSpinner";
import Navbar from "./components/navbar/Navbar";
import Homepage from "./screens/Homepage/Homepage";
import WhoWeAre from "./screens/AboutUs/WhoWeAre/WhoWeAre";
import Careers from "./screens/AboutUs/Careers/Careers";
import RegulatoryDocuments from "./screens/AboutUs/RegulatoryDocuments/RegulatoryDocuments";
import ContactUs from "./screens/AboutUs/ContactUs/ContactUs";
import Accounts from "./screens/Accounts/Accounts";
import Forex from "./screens/Markets/Forex/Forex";
import Cfd from "./screens/Markets/Cfd/Cfd";
import Stocks from "./screens/Markets/Stocks/Stocks";
import Platforms from "./screens/Platforms/Platforms";
import Offers from "./screens/Offers/Offers";
import Blog from "./screens/ResourcesSupport/Blog/Blog";
import MarketAnalysis from "./screens/ResourcesSupport/MarketAnalysis/MarketAnalysis";
import HelpCenter from "./screens/ResourcesSupport/HelpCenter/HelpCenter";
import AffiliateProgram from "./screens/affiliate-program/AffiliateProgram";
import TidioChatbot from "./components/tidio-chatbot/TidioChatbot";
import Footer from "./components/Footer/Footer";
import ForexLanding from "./screens/Landing/Forex/Forex";
import GoldLanding from "./screens/Landing/Gold/Gold";
import OilLanding from "./screens/Landing/Oil/Oil";
import StockLanding from "./screens/Landing/Stock/Stock";
import IslamicLanding from "./screens/Landing/Islamic/Islamic";
import Offer20Landing from "./screens/Landing/Offer20/BonusPromotion";
import Offer500Landing from "./screens/Landing/Offer20/500Promotion";
import Offer30Landing from "./screens/Landing/Offer20/30Promotion";
import OfferFreeSwapLanding from "./screens/Landing/Offer20/FreeSwapPromotion";



function useQuery() {
    return new URLSearchParams(useLocation().search);
}

function AppContent() {
    const [showSplashScreen, setShowSplashScreen] = useState(true);
    const [content, setContent] = useState({});
    const [loading, setLoading] = useState(true);
    const [language, setLanguage] = useState(localStorage.getItem('language'));
    const [direction, setDirection] = useState('ltr');

    const query = useQuery();
    const lang = query.get('lang');

    useEffect(() => {
        async function fetchData() {
            try {
                const [navbar, footer] =
                    await Promise.all([
                        getNavbar(),
                        getFooter()
                    ]);

                const navbarData = navbar.data.data[0];
                const footerData = footer.data.data[0];

                setContent({
                    navbar: navbarData,
                    footer: footerData
                });

                const availableLangs = ['en', 'ar'];
                let storedLanguage = '';

                if (lang && availableLangs.includes(lang)) {
                    storedLanguage = lang;
                    localStorage.setItem('language', lang);
                } else {
                    storedLanguage = localStorage.getItem('language') || 'en';
                }

                setLanguage(storedLanguage);

                const newDirection = storedLanguage === 'ar' ? 'rtl' : 'ltr';
                setDirection(newDirection);
                document.documentElement.setAttribute('dir', newDirection);

                setLoading(false);
            } catch (error) {
                console.error(error);
            }
        }

        fetchData();
    }, [lang]);

    function onVideoEndHandler() {
        setShowSplashScreen(false);
    }

    function onContactUsChatHandler() {
        if (window.tidioChatApi) {
            window.tidioChatApi.open();
        }
    }

    function onLanguageChangeHandler(language) {
        setLanguage(language);
        localStorage.setItem('language', language);

        const newDirection = language === 'ar' ? 'rtl' : 'ltr';
        setDirection(newDirection);
        document.documentElement.setAttribute("dir", newDirection);
    }

    return (
        <>
            <ScrollToTop />
            <div className='app-container' style={{
                '--color-green': Colors.green,
                '--color-green500': Colors.green500,
                '--color-green600': Colors.green600,
                '--color-green700': Colors.green700,
                '--color-black': Colors.black,
                '--color-gray': Colors.gray,
                '--color-darkgray': Colors.darkGray,
                '--color-darkgray500': Colors.darkGray500,
                '--color-darkgray-halfopacity': Colors.darkGrayHalfOpacity,
                '--color-darkgreen': Colors.darkGreen
            }}>
                {showSplashScreen ? (
                    <SplashScreen onVideoEnd={onVideoEndHandler} />
                ) : (
                    <>
                        {loading ? <LoadingSpinner /> : <Navbar navbar={content.navbar} language={language} onLanguageChange={onLanguageChangeHandler} />}
                        <div className='content-container'>
                            <Routes>
                                <Route path="*" element={<Navigate to="/" />} />
                                <Route path="/" element={<Homepage language={language} />} />
                                <Route path="/who-we-are" element={<WhoWeAre language={language} />} />
                                <Route path="/careers" element={<Careers language={language} />} />
                                <Route path='/regulatory-documents' element={<RegulatoryDocuments language={language} />} />
                                <Route path='/contact-us' element={<ContactUs setShowChat={onContactUsChatHandler} language={language} />} />
                                <Route path='/accounts' element={<Accounts language={language} />} />
                                <Route path='/markets/forex' element={<Forex language={language} />} />
                                <Route path='/markets/cfd' element={<Cfd language={language} />} />
                                <Route path='/markets/stocks' element={<Stocks language={language} />} />
                                <Route path='/platforms' element={<Platforms language={language} />} />
                                <Route path='/offers' element={<Offers language={language} />} />
                                <Route path='/resources/blog' element={<Blog language={language} />} />
                                <Route path='/resources/market-analysis' element={<MarketAnalysis language={language} />} />
                                <Route path='/resources/help-center' element={<HelpCenter language={language} />} />
                                <Route path='/affiliate-program' element={<AffiliateProgram language={language} />} />
                                <Route path='/affiliate-program' element={ <AffiliateProgram language={language} /> } />
                                <Route path="/forex" element={ <ForexLanding language={language} /> } />
                                <Route path="/gold" element={ <GoldLanding language={language} /> } />
                                <Route path="/oil" element={ <OilLanding language={language} /> } />
                                <Route path="/stock" element={ <StockLanding language={language} /> } />
                                <Route path="/islamic" element={ <IslamicLanding language={language} /> } />
                                <Route path="/offer" element={ <Offer20Landing language={language} /> } />
                                <Route path="/offer500" element={ <Offer500Landing language={language} /> } />
                                <Route path="/offer30" element={ <Offer30Landing language={language} /> } />
                                <Route path="/offerFreeSwap" element={ <OfferFreeSwapLanding language={language} /> } />
                            </Routes>
                        </div>
                        <TidioChatbot />
                        {loading ? <LoadingSpinner /> : <Footer footers={content.footer} language={language} />}
                    </>
                )}
            </div>
        </>
    );
};

export default AppContent;